import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { EnvironmentLoaderService } from './environment-loader.service';
import { PagedResult } from '../models/paging';
import {
  AccessGroup,
  AccessGroupUsersSearchEvent,
  AppInvitationsSearchEvent,
  ChargingUser,
  ChargingUserAppInvitation,
  ChargingUserSearchEvent,
  DownloadChargingUserReportRequest,
  PreviousAccessGroup,
  PreviousAccessGroupsSearchEvent,
  ScheduleAppInvitationRequest,
  UpdateIdTagInformationRequest,
  UpdateIdTagShippingAddress,
} from 'src/app/features/charging-users/charging-user.model';
import { PreviousAccessGroupDto } from 'src/app/features/charging-users/previous-access-group.dto';
import { InviteIdTagUserRequest } from 'src/app/features/charging-users/charging-user-create-modal/charging-user-create.model';
import { UpdateChargingUserRequest } from 'src/app/features/charging-users/charging-user-edit-modal/charging-user-edit.model';
import { CreateAccessGroupUserRequest } from 'src/app/features/charging-users/charging-user-accessgroups-modal/charging-user-accessgroups.model';
import { Guid } from 'guid-typescript';

@Injectable({
  providedIn: 'root',
})
export class ChargingUserService {
  private http = inject(HttpClient);
  private environmentLoaderService = inject(EnvironmentLoaderService);

  sendQueuedInvitationNow(chargingUserid: number, appInvitaitonIdentifier: string) {
    const url = `${this.apiURL}ChargingUser/${chargingUserid}/appInvitations/scheduled`;
    return this.http.patch(url, { invitationIdentifier: appInvitaitonIdentifier });
  }
  apiURL: string;
  protected httpHeaders: HttpHeaders;

  constructor() {
    this.apiURL = this.environmentLoaderService.config['serviceBaseUrl'] + '/api/v1/admin/';

    this.httpHeaders = new HttpHeaders({
      'Access-Control-Allow-Origin': this.apiURL,
      'Content-Type': 'application/json',
    });
  }

  inviteIdTagUser(model: InviteIdTagUserRequest) {
    const url = `${this.apiURL}ChargingUser/InviteIdTagUserToApp`;
    return this.http.post(url, model);
  }

  getChargingUsers(searchEvent?: ChargingUserSearchEvent): Observable<PagedResult<ChargingUser>> {
    const url = `${this.apiURL}ChargingUser`;
    let params = new HttpParams();
    const paging = searchEvent?.paging;
    const sorting = searchEvent?.sorting;

    if (searchEvent?.organizationId) {
      params = params.append('organizationId', searchEvent.organizationId);
    }
    if (searchEvent?.departmentId) {
      params = params.append('departmentId', searchEvent.departmentId);
    }
    if (searchEvent?.chargingUserStatuses) {
      searchEvent?.chargingUserStatuses.forEach(status => {
        if (status) {
          params = params.append('chargingUserStatuses', status.toString());
        }
      });
    }
    if (searchEvent?.idTagUserType) {
      params = params.append('idTagUserType', searchEvent.idTagUserType?.toString());
    }
    if (searchEvent?.chargingUserId) {
      params = params.append('chargingUserId', searchEvent.chargingUserId?.toString());
    }
    if (searchEvent?.query) {
      params = params.append('query', searchEvent.query);
    }
    if (paging?.pageIndex) {
      params = params.append('page', paging.pageIndex);
    }
    if (paging?.pageSize) {
      params = params.append('pageSize', paging.pageSize);
    }
    if (sorting?.active) {
      params = params.append('sortby', sorting.active);
    }
    if (sorting?.direction) {
      params = params.append('sortdirection', sorting.direction);
    }
    if (searchEvent?.excludedAccessGroupIdentifier) {
      params = params.append('excludedAccessGroupIdentifier', searchEvent.excludedAccessGroupIdentifier);
    }

    return this.http.get<PagedResult<ChargingUser>>(url, {
      params,
    });
  }

  getChargingUser(chargingUserId: number): Observable<ChargingUser> {
    const url = `${this.apiURL}ChargingUser/${chargingUserId}`;
    return this.http.get<ChargingUser>(url);
  }

  deleteChargingUser(chargingUserIdentifier: string) {
    const url = `${this.apiURL}ChargingUser/${chargingUserIdentifier}`;
    return this.http.delete(url);
  }

  createIdTagForChargingUser(idTag: CreateChargingUserIdTagRequest): Observable<number> {
    const url = `${this.apiURL}ChargingUser/${idTag.chargingUserId}/createIdTag`;

    return this.http.post<number>(url, {
      departmentId: idTag.departmentId,
      description: idTag.description,
      shippingAddress: idTag.shippingAddress,
      idToken: idTag.idToken,
      printedId: idTag.printedId,
      attention: idTag.attention,
      externalIdentifier: idTag.idTagIdentifier ?? null,
    });
  }

  changeOrganization(chargingUserId: number, request: SwitchChargingUserOrganizationRequest): Observable<number> {
    const url = `${this.apiURL}ChargingUser/${chargingUserId}/organization`;

    return this.http.patch<number>(url, request);
  }

  saveInvoicingAccountNumber(invoicingAccountNumber: string, chargingUserId: number): Observable<number> {
    const url = `${this.apiURL}ChargingUser/${chargingUserId}/invoicingAccountNumber`;

    return this.http.put<number>(url, {
      invoicingAccountNumber: invoicingAccountNumber,
    });
  }

  saveAgreementNumber(agreementNumber: string, chargingUserId: number): Observable<number> {
    const url = `${this.apiURL}ChargingUser/${chargingUserId}/agreementNumber`;

    return this.http.put<number>(url, {
      agreementNumber: agreementNumber,
    });
  }

  saveIdTagShippingAddress(updateIdTagShippingAddress: UpdateIdTagShippingAddress, chargingUserId: number): Observable<void> {
    const url = `${this.apiURL}ChargingUser/${chargingUserId}/shippingAddress`;

    return this.http.put<void>(url, updateIdTagShippingAddress);
  }

  updateIdTagInformation(chargingUserId: number, request: UpdateIdTagInformationRequest): Observable<void> {
    const url = `${this.apiURL}ChargingUser/${chargingUserId}/updateIdTagRelatedInfo`;
    return this.http.put<void>(url, request);
  }

  saveDepartment(departmentId: number, chargingUserId: number): Observable<void> {
    const url = `${this.apiURL}ChargingUser/${chargingUserId}/department`;

    return this.http.put<void>(url, {
      departmentId: departmentId,
    });
  }

  public downloadChargingUserReport(downloadReportModel: DownloadChargingUserReportRequest): Observable<Blob> {
    const url = `${this.apiURL}ChargingUser/ChargingUserReport`;
    return this.http.post<Blob>(url, downloadReportModel, {
      responseType: 'blob' as 'json',
    });
  }

  public scheduleAppInvitation(chargingUserId: number, request: ScheduleAppInvitationRequest) {
    const url = `${this.apiURL}ChargingUser/${chargingUserId}/AppInvitation/schedule`;
    return this.http.post(url, request);
  }

  public sendAppInvitation(chargingUserId: number, phoneNumber: string, phoneCountryCode: number): Observable<ChargingUserAppInvitation> {
    const url = `${this.apiURL}ChargingUser/${chargingUserId}/sendAppInvitation`;
    return this.http.post<ChargingUserAppInvitation>(url, { phoneNumber: phoneNumber, phoneCountryCode: phoneCountryCode });
  }

  getAppInvitations(searchEvent?: AppInvitationsSearchEvent): Observable<PagedResult<ChargingUserAppInvitation>> {
    const url = `${this.apiURL}ChargingUser/appInvitations`;
    let params = new HttpParams();
    const paging = searchEvent?.paging;
    const sorting = searchEvent?.sorting;

    if (searchEvent?.chargingUserIdentifier) {
      params = params.append('chargingUserIdentifier', searchEvent.chargingUserIdentifier?.toString());
    }
    if (searchEvent?.query) {
      params = params.append('query', searchEvent.query);
    }
    if (searchEvent?.appInvitationStatus) {
      params = params.append('appInvitationStatus', searchEvent.appInvitationStatus?.toString());
    }
    if (paging?.pageIndex) {
      params = params.append('page', paging.pageIndex);
    }
    if (paging?.pageSize) {
      params = params.append('pageSize', paging.pageSize);
    }
    if (sorting?.active) {
      params = params.append('sortby', sorting.active);
    }
    if (sorting?.direction) {
      params = params.append('sortdirection', sorting.direction);
    }

    return this.http.get<PagedResult<ChargingUserAppInvitation>>(url, {
      params,
    });
  }

  getAccessGroupUsers(searchEvent?: AccessGroupUsersSearchEvent): Observable<PagedResult<AccessGroup>> {
    if (!searchEvent?.chargingUserIdentifier) {
      throw new Error('chargingUserIdentifier is required.');
    }

    const url = `${this.apiURL}charging-user/${searchEvent.chargingUserIdentifier}/access-groups`;
    let params = new HttpParams();
    const paging = searchEvent.paging;
    const sorting = searchEvent.sorting;

    if (searchEvent.deviceId) {
      params = params.append('deviceId', searchEvent.deviceId);
    }
    if (searchEvent.mokUserNumber) {
      params = params.append('mokUserNumber', searchEvent.mokUserNumber);
    }
    if (paging?.pageIndex) {
      params = params.append('page', paging.pageIndex);
    }
    if (paging?.pageSize) {
      params = params.append('pageSize', paging.pageSize);
    }
    if (sorting?.active) {
      params = params.append('sortby', sorting.active);
    }
    if (sorting?.direction) {
      params = params.append('sortdirection', sorting.direction);
    }
    if (searchEvent.includeDeleted) {
      params = params.append('includeDeleted', searchEvent.includeDeleted);
    }
    return this.http.get<PagedResult<AccessGroup>>(url, {
      params,
    });
  }

  getPreviousAccessGroups(searchEvent: PreviousAccessGroupsSearchEvent): Observable<PagedResult<PreviousAccessGroup>> {
    const url = `${this.apiURL}charging-users/${searchEvent.chargingUserId}/access-groups/previous`;
    let params = new HttpParams();
    if (searchEvent.paging?.pageIndex) {
      params = params.append('page', searchEvent.paging.pageIndex);
    }
    if (searchEvent.paging?.pageSize) {
      params = params.append('page_size', searchEvent.paging.pageSize);
    }
    return this.http.get<PagedResult<PreviousAccessGroupDto>>(url, { params }).pipe(
      map(result => ({
        ...result,
        items: result.items.map(this.mapPreviousAccessGroupDtoToModel),
      }))
    );
  }

  private mapPreviousAccessGroupDtoToModel = (dto: PreviousAccessGroupDto): PreviousAccessGroup => {
    return {
      name: dto.name,
      created: new Date(dto.created),
      deleted: dto.deleted ? new Date(dto.deleted) : undefined,
      discountAmount: dto.discount_amount,
      discountType: dto.discount_type,
      accessType: dto.access_type,
      activeFrom: dto.active_from ? new Date(dto.active_from) : undefined,
      activeTo: dto.active_to ? new Date(dto.active_to) : undefined,
    };
  };

  deleteAccessGroupUser(chargingUserIdentifier: string) {
    const url = `${this.apiURL}access-group-user/${chargingUserIdentifier}`;
    return this.http.delete(url);
  }

  createAccessGroupUser(request: CreateAccessGroupUserRequest): Observable<Guid> {
    const url = `${this.apiURL}access-group-user`;
    return this.http.post<Guid>(url, request);
  }

  updateChargingUser(identifier: string, request: UpdateChargingUserRequest): Observable<void> {
    const url = `${this.apiURL}charging-user/${identifier}`;

    return this.http.put<void>(url, request, {
      headers: this.httpHeaders,
    });
  }
}

export interface CreateChargingUserIdTagRequest {
  chargingUserId: number;
  departmentId: number | null;
  description: string;
  shippingAddress: string;
  idToken: string;
  printedId: number;
  attention: string;
  idTagIdentifier: string | null;
}

export interface SwitchChargingUserOrganizationRequest {
  organizationId: number;
  departmentId: number;
  accessGroupIdentifiers: string[];
}
